import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal } from "../common/Modal/Modal";
import { CardSelected } from "../../type";
import { ContentWrapper, NoProductFound } from "./modalPricing.styled";
import { Cards } from "../Cards/Cards";
import {
  LabelStyled,
  PrimaryButton,
  SecondaryButton,
  StyledInput,
} from "../common/common.style";
import { CardTraderTable } from "./CardTraderTable";
import { CardMarketTable } from "./CardMarketTable";
import Loader from "../common/Loader/Loader";
import { Box, FlexBox } from "../primitives";
import { CardMarketCardTraderSwitch } from "../CardMarketCardTraderSwitch/CardMarketCardTraderSwitch";
import { useGetProductPriceDataQuery } from "../../services/myServer/cardTraderProduct";

interface ModalPricingProps {
  setOpenModalChoosePrice: (arg1: string | null) => void;
  cardSelected: CardSelected;
  setCardsSelected: (arg1: CardSelected | null) => void;
  setCardsBoard: (arg1: CardSelected[]) => void;
  openModalChoosePrice: string | null;
  cardsBoard: CardSelected[];
}

export const ModalPricing = ({
  setOpenModalChoosePrice,
  cardSelected,
  setCardsSelected,
  setCardsBoard,
  openModalChoosePrice,
  cardsBoard,
}: ModalPricingProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [priceType, setPriceType] = useState<"cardtrader" | "cardmarket">(
    "cardtrader"
  );
  const { data: pricingData, isLoading: isApiLoading } =
    useGetProductPriceDataQuery({ bluprintId: cardSelected.id });

  const cardTraderCards = useMemo(() => {
    if (pricingData) {
      if (cardSelected.finish === "foil") {
        return pricingData?.cardTraderPrice?.productFoil;
      }

      return pricingData?.cardTraderPrice?.productRegular;
    }

    return [];
  }, [cardSelected.finish, pricingData]);

  const cardMarketCards = useMemo(() => {
    if (pricingData) {
      if (cardSelected.finish === "foil") {
        return pricingData?.cardMarketPrice?.productFoil;
      }

      return pricingData?.cardMarketPrice?.productRegular;
    }

    return [];
  }, [cardSelected.finish, pricingData]);

  const commonConfirmEnter = useCallback(() => {
    setCardsBoard(
      cardsBoard.map((card, index) =>
        card.uid === openModalChoosePrice
          ? {
              ...(cardSelected as CardSelected),
              hasBeenEdited:
                card.originalPrice === cardSelected.price ? false : true,
            }
          : { ...card }
      )
    );
    setCardsSelected(null);
    setOpenModalChoosePrice(null);
  }, [
    setCardsBoard,
    cardsBoard,
    setCardsSelected,
    setOpenModalChoosePrice,
    openModalChoosePrice,
    cardSelected,
  ]);

  const commonCancelEsc = useCallback(() => {
    setCardsSelected(null);
    setOpenModalChoosePrice(null);
  }, [setCardsSelected, setOpenModalChoosePrice]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        commonCancelEsc();
      } else if (e.key === "Enter") {
        commonConfirmEnter();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [
    setOpenModalChoosePrice,
    setCardsSelected,
    commonConfirmEnter,
    commonCancelEsc,
  ]);

  return (
    <Modal
      onClose={() => {
        commonCancelEsc();
      }}
      modalStyle={{ height: "90vh", width: "80vw", maxWidth: "100%" }}
    >
      <ContentWrapper>
        {isApiLoading ? (
          <Loader />
        ) : (
          <Box css={{ width: "100%" }}>
            <CardMarketCardTraderSwitch
              selected={priceType}
              setSelected={setPriceType}
            />
            {pricingData && priceType === "cardmarket" && (
              <Box>
                <CardMarketTable
                  cardMarketCards={cardMarketCards}
                  cardMarketPrice={pricingData?.cardMarketPrice}
                  cardSelected={cardSelected}
                  setCardsSelected={setCardsSelected}
                  editedAt={pricingData?.cardMarketPrice?.updated_at}
                />
              </Box>
            )}
            {pricingData && priceType === "cardtrader" && (
              <Box>
                {cardTraderCards?.length ? (
                  <CardTraderTable
                    cardTraderCards={cardTraderCards}
                    userExtraData={pricingData?.cardTraderPrice?.userExtraData}
                    cardSelected={cardSelected}
                    setCardsSelected={setCardsSelected}
                    editedAt={pricingData?.cardTraderPrice?.updated_at}
                  />
                ) : (
                  <NoProductFound>no product found</NoProductFound>
                )}
              </Box>
            )}
          </Box>
        )}
        <FlexBox css={{ flexDirection: "column", gap: 10 }}>
          <Cards card={cardSelected} />
          <Box>
            <LabelStyled>Price</LabelStyled>
            <StyledInput
              ref={inputRef}
              type="number"
              min={0}
              value={cardSelected.price}
              onChange={(e) => {
                setCardsSelected({
                  ...cardSelected,
                  price: +e.target.value || 1,
                  hasPriceIssue: false,
                  uploadingError: "",
                });
              }}
              onFocus={(e) => e.target.select()}
              css={{ width: 330, marginTop: 10 }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </Box>
        </FlexBox>
      </ContentWrapper>

      <FlexBox
        css={{
          justifyContent: "center",
          alignItems: "center",
          gap: 20,
          marginTop: 20,
        }}
      >
        <PrimaryButton onClick={commonConfirmEnter}>
          CONFIRM ( ENTER )
        </PrimaryButton>
        <SecondaryButton onClick={commonCancelEsc}>
          CANCEL ( ESC )
        </SecondaryButton>
      </FlexBox>
    </Modal>
  );
};
